import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Star } from 'lucide-react';

export const AddReviewForm = ({ onReviewAdded }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (comment.trim().length < 10) {
      toast.error('Please write a review of at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        rating,
        comment: comment.trim()
      });
      toast.success('Thank you for your review!');
      setRating(0);
      setComment('');
      if (onReviewAdded) onReviewAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-card p-6 rounded-lg" data-testid="add-review-form">
      <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Share Your Experience</h3>
      <p className="text-sm text-muted-foreground mb-6">Help others by sharing your rental experience with Amjed Tours</p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Your Rating</label>
          <div className="flex gap-1" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="transition-transform hover:scale-110"
                data-testid={`rating-star-${star}`}
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= (hoverRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Your Review</label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us about your experience..."
            rows={4}
            maxLength={500}
            data-testid="review-comment-input"
          />
          <p className="text-xs text-muted-foreground mt-1">{comment.length}/500 characters</p>
        </div>

        <Button
          type="submit"
          className="w-full btn-primary"
          disabled={submitting}
          data-testid="submit-review-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </form>
    </div>
  );
};
